/**
 * Credit Management Simulator — data layer.
 * All numbers are educational approximations for the simulated HeirWise
 * credit profile. Nothing here affects real credit bureaus or real money.
 */

export const STARTING_PROFILE = {
  score: 680,
  creditLimit: 2_000,
  balance: 0,
  apr: 22.99,
  checking: 650,
  daysToDue: 7,
  interestAccrued: 0,
  lateFees: 0,
  missedPayments: 0,
  paymentsMade: 0,
  autopay: "none" as "none" | "minimum" | "full",
  lateRisk: 35, // 0–100, higher = more likely to miss
};

export type SimProfile = typeof STARTING_PROFILE;

export interface DecisionImpact {
  key: string;
  label: string;
  summary: string;
  bullets: string[];
}

export const SPENDING_CHOICES: DecisionImpact[] = [
  {
    key: "essentials",
    label: "A. Use card for $120 essentials",
    summary: "Groceries and gas — practical needs",
    bullets: ["Balance +$120", "Utilization +6%", "Manageable payoff", "Small positive if paid on time"],
  },
  {
    key: "phone",
    label: "B. Buy a $600 phone upgrade",
    summary: "A want — convenient but pricey",
    bullets: ["Balance +$600", "Utilization +30%", "Score risk if not paid down quickly"],
  },
  {
    key: "shopping",
    label: "C. Spend $1,400 on shopping & entertainment",
    summary: "Lifestyle creep — large discretionary spend",
    bullets: ["Balance +$1,400", "Utilization +70%", "High credit risk", "Score drops"],
  },
  {
    key: "max",
    label: "D. Max out the card at $2,000",
    summary: "All-in — maximum exposure",
    bullets: ["Balance +$2,000", "Utilization 100%", "Major score drop", "Cash pressure rises"],
  },
];

export const SPENDING_AMOUNT: Record<string, number> = {
  essentials: 120, phone: 600, shopping: 1_400, max: 2_000,
};

export const PAYMENT_CHOICES: DecisionImpact[] = [
  {
    key: "full",
    label: "A. Pay full statement balance",
    summary: "Avoid interest entirely",
    bullets: ["Deducts full amount from checking", "Interest avoided", "Score improves"],
  },
  {
    key: "minimum",
    label: "B. Pay only the minimum",
    summary: "Cheapest now, costly later",
    bullets: ["Small cash hit", "Interest grows", "Score pressure stays"],
  },
  {
    key: "partial",
    label: "C. Pay half of the balance",
    summary: "Moderate payoff",
    bullets: ["Some interest still accrues", "Balance decreases", "Moderate outcome"],
  },
  {
    key: "skip",
    label: "D. Skip the payment",
    summary: "Risky — no cash leaves now",
    bullets: ["Late fee added", "Major score drop", "Payment history damaged"],
  },
];

export const AUTOPAY_CHOICES: DecisionImpact[] = [
  {
    key: "full",
    label: "A. Autopay full balance",
    summary: "Strongest protection",
    bullets: ["Prevents missed payments", "Needs enough cash", "Best score outcome"],
  },
  {
    key: "minimum",
    label: "B. Autopay the minimum",
    summary: "Safety net only",
    bullets: ["Prevents late payment", "Interest may continue", "Safer for cash flow"],
  },
  {
    key: "reminder",
    label: "C. Calendar reminder only",
    summary: "Manual approach",
    bullets: ["No automation", "Can fail if you get distracted", "Moderate late risk"],
  },
  {
    key: "none",
    label: "D. No reminder, just remember",
    summary: "High late-payment risk",
    bullets: ["Memory only", "Late payments likely over time"],
  },
];

export const RECOVERY_CHOICES: DecisionImpact[] = [
  {
    key: "pay_autopay",
    label: "A. Pay immediately and set autopay",
    summary: "Strongest recovery",
    bullets: ["Stops further damage", "Cash decreases now", "Future risk drops"],
  },
  {
    key: "minimum",
    label: "B. Pay the minimum and keep spending",
    summary: "Partial recovery",
    bullets: ["Stops late fee compounding", "Interest continues", "Risk remains"],
  },
  {
    key: "courtesy",
    label: "C. Call lender and request a one-time courtesy reversal",
    summary: "Lender may forgive the fee once",
    bullets: ["Possible fee reversal", "Still requires a payment plan"],
  },
  {
    key: "ignore",
    label: "D. Ignore the late notice",
    summary: "Most damaging path",
    bullets: ["More fees", "Score drops further", "Account risk grows"],
  },
];

export const PAYOFF_CHOICES: DecisionImpact[] = [
  {
    key: "avalanche",
    label: "A. Debt Avalanche",
    summary: "Pay highest APR first",
    bullets: ["Saves the most interest", "Mathematically strongest"],
  },
  {
    key: "snowball",
    label: "B. Debt Snowball",
    summary: "Pay smallest balance first",
    bullets: ["Builds psychological momentum", "Slightly more interest paid overall"],
  },
  {
    key: "split",
    label: "C. Split evenly across all debts",
    summary: "Feels fair, slower progress",
    bullets: ["No debt closes quickly", "More interest than avalanche"],
  },
  {
    key: "cash",
    label: "D. Pay none — keep the extra cash",
    summary: "Preserves cash, debt grows",
    bullets: ["Interest keeps compounding", "Credit pressure stays"],
  },
];

export interface SimDebt { name: string; balance: number; apr: number }
export const STARTING_DEBTS: SimDebt[] = [
  { name: "Credit Card A", balance: 800, apr: 22.99 },
  { name: "Store Card",    balance: 300, apr: 28.99 },
  { name: "Personal Loan", balance: 1200, apr: 12.50 },
];
export const PAYOFF_EXTRA = 250;

export interface GlossaryTerm { term: string; def: string }
export const GLOSSARY: GlossaryTerm[] = [
  { term: "Credit Score (Simulated)", def: "A practice number from 300–850 that reflects in-module credit behavior. It does not affect real credit bureaus." },
  { term: "Credit Limit", def: "The maximum balance a credit card lets you carry at one time." },
  { term: "Utilization", def: "Current balance ÷ credit limit. Lower is better — under 30% is healthy, under 10% is excellent." },
  { term: "APR", def: "Annual Percentage Rate — the yearly cost of carrying a balance, expressed as a percentage." },
  { term: "Statement Balance", def: "The full balance you owe at the end of a billing cycle. Pay it in full to avoid interest." },
  { term: "Minimum Payment", def: "The smallest amount required to keep the account in good standing. Paying only this lets interest pile up." },
  { term: "Autopay", def: "An automatic payment scheduled from your checking account to avoid late fees and missed payments." },
  { term: "Late Fee", def: "A charge added when a payment is not made by the due date. Often $25–$40." },
  { term: "Payment History", def: "Your record of paying on time. Usually the single most important factor in a credit score." },
  { term: "Debt Avalanche", def: "Paying the highest-APR debt first to minimize interest." },
  { term: "Debt Snowball", def: "Paying the smallest balance first to build momentum." },
];

/* ─────────── Scoring math ─────────── */

export interface SimState extends SimProfile {
  // running accumulators
  scoreHistory: Array<{ reason: string; delta: number; score: number }>;
  bestDecision?: string;
  worstDecision?: string;
  worstDelta: number;
  bestDelta: number;
  lateEventTriggered: boolean;
  payoffStrategy?: string;
  interestSaved: number;
}

export const initialState = (checkingFromAccount?: number): SimState => ({
  ...STARTING_PROFILE,
  checking: checkingFromAccount ?? STARTING_PROFILE.checking,
  scoreHistory: [{ reason: "Starting score", delta: 0, score: STARTING_PROFILE.score }],
  worstDelta: 0,
  bestDelta: 0,
  lateEventTriggered: false,
  interestSaved: 0,
});

export function applyDelta(s: SimState, reason: string, delta: number, label?: string): SimState {
  const score = Math.max(300, Math.min(850, s.score + delta));
  const out: SimState = {
    ...s,
    score,
    scoreHistory: [...s.scoreHistory, { reason, delta, score }],
  };
  if (delta < out.worstDelta) { out.worstDelta = delta; out.worstDecision = label ?? reason; }
  if (delta > out.bestDelta) { out.bestDelta = delta; out.bestDecision = label ?? reason; }
  return out;
}

export function utilizationPct(balance: number, limit: number) {
  return limit > 0 ? Math.round((balance / limit) * 100) : 0;
}

export function minPaymentFor(balance: number) {
  if (balance <= 0) return 0;
  return Math.max(25, Math.round(balance * 0.03));
}

export function scoreTierLabel(score: number): { label: string; tone: "good" | "warn" | "bad" | "default" } {
  if (score >= 750) return { label: "Excellent", tone: "good" };
  if (score >= 700) return { label: "Good", tone: "good" };
  if (score >= 650) return { label: "Fair", tone: "default" };
  if (score >= 580) return { label: "Building", tone: "warn" };
  return { label: "At Risk", tone: "bad" };
}

export function behaviorRating(s: SimState): string {
  const change = s.score - STARTING_PROFILE.score;
  if (s.missedPayments > 0 && change <= -40) return "Late Payment Recovery";
  if (s.balance >= s.creditLimit * 0.7) return "High Utilization Risk";
  if (s.payoffStrategy === "cash" && s.balance > 0) return "Debt Spiral Warning";
  if (s.payoffStrategy === "minimum") return "Minimum Payment Trapper";
  if (change >= 20) return "Responsible Borrower";
  if (change >= 5) return "Credit Builder";
  return "Needs Practice";
}